import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";

// Bespoke landing for HostPilot: PHP Server & SQL (Android).
// Same warm-paper shell as the other app landings (site-wide theme), with the
// app's server green used only inside the live-log widget so the page still
// reads as Cosmovex rather than a one-off. Rigorous SEO (meta + OG + JSON-LD).
// Hosted at /apps/hostpilot. Assets in /hostpilot/landing.
//
// COPY RULE: HostPilot's Pro is sold in more than one form, so this page never
// characterises the billing model — it says "optional Pro upgrade" and stops.
// Do NOT copy the "no subscription" line from the other landings onto this app.

const PLAY = "https://play.google.com/store/apps/details?id=com.cosmovex.hostpilot";
const AMBER = "#A8720E";
const AMBER_D = "#D9A32E";
const BG = "#FBFAF6";
const PANEL = "#FFFFFF";
const INK = "#26313B";
const MUTE = "#5B6B7C";
const SERVER_GREEN = "#1F8A4C";
const B = "/hostpilot/landing";
const SANS = `-apple-system, "SF Pro Display", "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
const MONO = `"SF Mono", "JetBrains Mono", "Fira Code", ui-monospace, Menlo, Consolas, monospace`;

const playClick = (e: React.MouseEvent) => {
  e.preventDefault();
  const w = window as any;
  if (w.gtag_report_conversion) w.gtag_report_conversion(PLAY);
  else window.open(PLAY, "_blank", "noopener");
};

const PlayButton: React.FC<{ small?: boolean }> = ({ small }) => (
  <a href={PLAY} onClick={playClick} aria-label="Get HostPilot on Google Play"
    style={{
      display: "inline-flex", alignItems: "center", gap: "0.6rem",
      padding: small ? "0.7rem 1.3rem" : "1.05rem 2rem",
      background: "#E8B44C", color: "#221A06",
      borderRadius: "0.9rem", fontWeight: 700, fontSize: small ? "0.98rem" : "1.18rem",
      textDecoration: "none", border: `1px solid ${AMBER_D}`,
      boxShadow: `0 1px 2px rgba(52,44,26,0.06), 0 4px 14px rgba(52,44,26,0.05)`, fontFamily: SANS,
    }}>
    <svg width={small ? 18 : 22} height={small ? 18 : 22} viewBox="0 0 24 24" fill="currentColor">
      <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.2l2.807 1.626a1 1 0 010 1.734l-2.808 1.626L15.206 12l2.492-2.493zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" />
    </svg>
    Get it on Google Play
  </a>
);

// ── Live access log ─────────────────────────────────────────────────────────
// The product's whole promise is "it is still serving". A log that keeps
// arriving while an uptime counter climbs says that better than a sentence.
const HITS = [
  { m: "GET", p: "/", s: 200, ip: "192.168.1.14" },
  { m: "GET", p: "/style.css", s: 200, ip: "192.168.1.14" },
  { m: "POST", p: "/api/items", s: 201, ip: "192.168.1.22" },
  { m: "GET", p: "/api/health", s: 200, ip: "192.168.1.31" },
  { m: "POST", p: "/webhooks/stripe", s: 200, ip: "192.168.1.9" },
  { m: "GET", p: "/uploads/notes.pdf", s: 200, ip: "192.168.1.22" },
  { m: "PUT", p: "/api/items/42", s: 204, ip: "192.168.1.31" },
  { m: "GET", p: "/favicon.ico", s: 404, ip: "192.168.1.14" },
];

const METHOD_TINT: Record<string, string> = {
  GET: SERVER_GREEN, POST: "#0F6BBF", PUT: AMBER, DELETE: "#B3261E",
};

const clock = (s: number) =>
  `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor(s / 60) % 60).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const AccessLog: React.FC = () => {
  const [rows, setRows] = useState<number[]>([0]);
  const [up, setUp] = useState(7364);
  const reduce = useRef(false);

  useEffect(() => {
    reduce.current = typeof window !== "undefined" &&
      !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const tick = window.setInterval(() => setUp((s) => s + 1), 1000);
    if (reduce.current) {
      setRows([0, 1, 2, 3, 4]);
      return () => clearInterval(tick);
    }
    const feed = window.setInterval(
      () => setRows((r) => [...r, (r[r.length - 1] + 1) % HITS.length].slice(-5)), 1500);
    return () => { clearInterval(tick); clearInterval(feed); };
  }, []);

  return (
    <div style={{
      background: "#F1EEE5", borderRadius: 14, border: "1px solid #E1DCCE",
      boxShadow: `0 1px 2px rgba(52,44,26,0.06), 0 4px 14px rgba(52,44,26,0.05)`,
      overflow: "hidden", fontFamily: MONO, width: "100%", maxWidth: 480,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "0.7rem 0.9rem", borderBottom: "1px solid #E1DCCE" }}>
        <span aria-hidden style={{ width: 9, height: 9, borderRadius: 99, background: SERVER_GREEN, boxShadow: `0 0 0 3px rgba(31,138,76,0.16)` }} />
        <span style={{ color: SERVER_GREEN, fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.04em" }}>RUNNING</span>
        <span style={{ marginLeft: "auto", color: MUTE, fontSize: "0.78rem" }}>up {clock(up)}</span>
      </div>
      <div style={{ padding: "0.9rem 1rem 1.1rem", fontSize: "0.86rem", lineHeight: 1.85, minHeight: 168 }}>
        <div style={{ color: MUTE, marginBottom: 4 }}>http://192.168.1.7:8080 · access log</div>
        {rows.map((h, i) => (
          <div key={`${h}-${i}`} className="hp-row" style={{ display: "flex", gap: 10, whiteSpace: "nowrap" }}>
            <span style={{ color: METHOD_TINT[HITS[h].m] || INK, fontWeight: 700, width: 46, display: "inline-block" }}>{HITS[h].m}</span>
            <span style={{ color: INK, flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{HITS[h].p}</span>
            <span style={{ color: HITS[h].s >= 400 ? "#B3261E" : SERVER_GREEN }}>{HITS[h].s}</span>
            <span style={{ color: MUTE }}>{HITS[h].ip}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Phone: React.FC<{ src: string; alt: string; w?: number }> = ({ src, alt, w = 250 }) => (
  <div style={{
    width: w, borderRadius: w * 0.12, background: "#05070b", padding: w * 0.022,
    border: "1px solid rgba(38,49,59,0.10)",
    boxShadow: `0 30px 60px -18px rgba(52,44,26,0.20), 0 10px 24px rgba(52,44,26,0.10)`,
  }}>
    <div style={{ borderRadius: w * 0.1, overflow: "hidden", background: "#05070b", lineHeight: 0 }}>
      <img src={src} alt={alt} loading="lazy" style={{ width: "100%", height: "auto", display: "block" }} />
    </div>
  </div>
);

const FeatureCard: React.FC<{ src: string; label: string; sub: string }> = ({ src, label, sub }) => (
  <div style={{
    display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center",
    background: PANEL, border: "1px solid #E1DCCE", borderRadius: "1.5rem", padding: "2rem 1.3rem 1.7rem",
    boxShadow: "0 1px 2px rgba(52,44,26,0.06), 0 4px 14px rgba(52,44,26,0.05)",
  }}>
    <div style={{
      background: "#F1EEE5", borderRadius: "1.4rem", padding: "1.6rem 1rem 0", width: "100%",
      display: "flex", justifyContent: "center", overflow: "hidden", maxHeight: 400,
    }}>
      <Phone src={src} alt={label} w={210} />
    </div>
    <h3 style={{ fontFamily: SANS, fontWeight: 700, fontSize: "1.4rem", color: INK, margin: "1.3rem 0 0.4rem" }}>{label}</h3>
    <p style={{ fontFamily: SANS, fontSize: "1rem", color: MUTE, lineHeight: 1.5, margin: 0, maxWidth: 300 }}>{sub}</p>
  </div>
);

const Section: React.FC<{ title: React.ReactNode; sub: string; children: React.ReactNode }> = ({ title, sub, children }) => (
  <section style={{ maxWidth: 1180, margin: "0 auto", padding: "3.5rem 1.5rem" }}>
    <h2 style={{ fontFamily: SANS, fontWeight: 800, fontSize: "clamp(2rem,5vw,3rem)", letterSpacing: "-1.5px", color: INK, margin: 0 }}>{title}</h2>
    <p style={{ fontFamily: SANS, fontSize: "1.18rem", color: MUTE, margin: "0.8rem 0 2.6rem", maxWidth: 640, lineHeight: 1.5 }}>{sub}</p>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.6rem" }}>{children}</div>
  </section>
);

const Accent: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{ color: AMBER }}>{children}</span>
);

// Real, query-matched FAQs — also emitted as FAQPage structured data.
const FAQS = [
  { q: "Can I run a web server on my Android phone?", a: "Yes. HostPilot serves a folder over your Wi-Fi or on localhost. Start it, scan the QR code from another device, and your site is live on your network — no laptop and no cloud in between." },
  { q: "Does the server keep running in the background?", a: "Yes. HostPilot runs under an Android foreground service with a persistent notification, so it keeps serving while you switch apps or the screen is off. A live uptime counter shows how long it has been up, and a guided reliability checklist walks you through the battery settings your specific device needs." },
  { q: "Can I really run PHP on a phone?", a: "Yes. PHP 8.4 runs your .php files directly on the device, with SQLite databases created for you on first run. Ready-made starters include a JSON REST API and a shareable guestbook." },
  { q: "Can I browse and query my database?", a: "Yes. A visual SQL console runs in the browser: run queries, browse tables, edit rows, and import or export data. A built-in MySQL and MariaDB client also connects out to servers elsewhere." },
  { q: "What is File Drop?", a: "A Wi-Fi file server. Share a link and anyone on your network can send files straight to your phone — useful for collecting work from friends, clients or students without putting a cloud service in the middle." },
  { q: "Can I catch webhooks with it?", a: "Yes. The Request Inspector shows every request as it arrives — method, path, headers and body — so you can point a webhook or an HTML form at your phone and watch it land live." },
  { q: "Is my data uploaded anywhere?", a: "No. HostPilot asks only for the folders you choose — scoped storage, not the all-files permission. There is no account and no cloud. Your files and databases stay on your device and are served over your own network, only while the server is on." },
  { q: "Is HostPilot free?", a: "It is free to download, and the reliability core is in the free tier — foreground service, uptime counter, QR sharing, the live access log, the editor, File Drop and the Request Inspector. An optional Pro upgrade removes ads and unlocks the database console beyond its free runs, your full inbox history, password protection, browser upload, custom port and network mode, and unlimited sites." },
];

const HostPilotLanding: React.FC = () => {
  const [showBar, setShowBar] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowBar(window.scrollY > 640);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "HostPilot: PHP Server & SQL",
    operatingSystem: "Android 7.0+",
    applicationCategory: "DeveloperApplication",
    applicationSubCategory: "Web Server",
    url: "https://cosmovexapps.com/apps/hostpilot",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    description: "A web server for Android that stays running. Host a site from a folder, run PHP 8.4 with SQLite, query your database from any browser on your Wi-Fi, collect files through a link, and watch webhooks arrive live.",
    featureList: [
      "Web server for Android that keeps running in the background",
      "PHP 8.4 on-device with SQLite",
      "Visual SQL console in the browser",
      "MySQL and MariaDB client",
      "File Drop — collect files over Wi-Fi",
      "Request Inspector for webhooks and forms",
      "Live access log with status codes and client IPs",
      "Built-in editor with version history",
    ],
    downloadUrl: PLAY,
    image: "https://cosmovexapps.com" + B + "/og.png",
    publisher: { "@type": "Organization", name: "Cosmovex", url: "https://cosmovexapps.com" },
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://cosmovexapps.com/" },
      { "@type": "ListItem", position: 2, name: "Apps", item: "https://cosmovexapps.com/apps" },
      { "@type": "ListItem", position: 3, name: "HostPilot: PHP Server & SQL", item: "https://cosmovexapps.com/apps/hostpilot" },
    ],
  };

  return (
    <>
      <Helmet>
        <title>Web Server for Android — Run PHP 8.4 &amp; SQL | HostPilot</title>
        <meta name="description" content="Turn your Android phone into a web server that stays running. Host a site from a folder, run PHP 8.4 with SQLite, query your database from any browser on your Wi-Fi, and watch webhooks arrive live." />
        <link rel="canonical" href="https://cosmovexapps.com/apps/hostpilot" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="HostPilot — a web server for Android that stays running" />
        <meta property="og:description" content="Host a site, run PHP 8.4 and SQLite, and keep serving while you switch apps or the screen is off. Your files never leave your device." />
        <meta property="og:image" content={"https://cosmovexapps.com" + B + "/og.png"} />
        <meta property="og:url" content="https://cosmovexapps.com/apps/hostpilot" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="theme-color" content="#FBFAF6" />
        <meta name="keywords" content="web server android, php server android, run php on phone, localhost server android, sqlite console android, wifi file transfer, webhook tester phone, android http server, mysql client android" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </Helmet>

      <div style={{ background: BG, minHeight: "100vh", fontFamily: SANS, position: "relative", overflow: "hidden" }}>
        {/* NAV */}
        <nav style={{ position: "relative", maxWidth: 1180, margin: "0 auto", padding: "1.4rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.7rem" }}>
            <img src={B + "/logo.png"} alt="HostPilot logo" width={42} height={42} style={{ borderRadius: 11 }} />
            <span style={{ fontWeight: 700, fontSize: "1.08rem", color: INK }}>HostPilot</span>
          </div>
          <PlayButton small />
        </nav>

        {/* HERO */}
        <header className="hp-hero" style={{ position: "relative", maxWidth: 1180, margin: "0 auto", padding: "2.5rem 1.5rem 1.5rem", display: "grid", gridTemplateColumns: "1.05fr 0.95fr", gap: "2.5rem", alignItems: "center" }}>
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", background: "#F1EEE5", color: AMBER, fontWeight: 700, fontSize: "0.9rem", padding: "0.45rem 0.9rem", borderRadius: 999, border: "1px solid #E1DCCE", marginBottom: "1.3rem", fontFamily: MONO }}>
              php 8.4 · sqlite · stays running
            </div>
            <h1 style={{ fontWeight: 800, fontSize: "clamp(2.5rem,6vw,4.2rem)", lineHeight: 1.04, letterSpacing: "-2.5px", color: INK, margin: 0 }}>
              Your phone is the <Accent>server</Accent>.
            </h1>
            <p style={{ fontSize: "1.3rem", color: "#3D4B59", lineHeight: 1.45, margin: "1.3rem 0 1.8rem", maxWidth: 520 }}>
              Host a site, run PHP 8.4 with SQLite behind it, and query your database from any browser on your Wi-Fi. <strong style={{ color: INK }}>It keeps serving while you switch apps or the screen is off.</strong>
            </p>
            <div style={{ marginBottom: "1.8rem", maxWidth: 480 }}><AccessLog /></div>
            <PlayButton />
            <p style={{ color: MUTE, fontSize: "0.92rem", margin: "0.9rem 0 0", fontFamily: MONO }}># free to start · no account · nothing leaves your device</p>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Phone src={B + "/hero_screen.png"} alt="HostPilot dashboard showing a running server, its URL and a live uptime counter" w={300} />
          </div>
        </header>

        {/* WHAT IT CAN BE */}
        <Section title="Five things it can be" sub="One server, started the same way every time — what it serves is up to you.">
          <FeatureCard src={B + "/share.png"} label="Web server" sub="Host a site from a folder — HTML, CSS, JavaScript, images and video — then share the URL or its QR code." />
          <FeatureCard src={B + "/sql.png"} label="Database console" sub="Run queries, browse tables and edit rows from any browser on your Wi-Fi, backed by SQLite on the device." />
          <FeatureCard src={B + "/filedrop.png"} label="File Drop" sub="Share a link and collect files from anyone on your network, straight to your phone." />
        </Section>

        {/* FOR DEVELOPERS */}
        <Section title={<>Built for people who <Accent>ship</Accent></>} sub="The tools you'd expect from a real server, on the device in your hand.">
          <FeatureCard src={B + "/inspector.png"} label="Request Inspector" sub="Point a webhook or a form at your phone and watch every request arrive live — method, path, headers and body." />
          <FeatureCard src={B + "/editor.png"} label="Built-in editor" sub="Syntax highlighting for HTML, CSS, JS, PHP, SQL and more, with find & replace and version history." />
          <FeatureCard src={B + "/hero_screen.png"} label="Live uptime & access log" sub="Every request with method, path, status code and client IP — and a counter showing how long you've been up." />
        </Section>

        {/* PRIVACY */}
        <section style={{ position: "relative", margin: "2rem 1.5rem", padding: "3.5rem 1.5rem", maxWidth: 1100, marginInline: "auto", background: "#F1EEE5", border: "1px solid #E1DCCE", borderRadius: "2rem" }}>
          <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
            <h2 style={{ fontWeight: 800, fontSize: "clamp(1.9rem,5vw,2.9rem)", letterSpacing: "-1.5px", color: INK, margin: 0 }}>
              No account. No cloud.<br />Your files stay on your <Accent>device</Accent>.
            </h2>
            <p style={{ fontSize: "1.18rem", color: "#3D4B59", lineHeight: 1.55, margin: "1.3rem auto 2rem", maxWidth: 620 }}>
              HostPilot asks only for the folders you choose — scoped storage, not the all-files permission. Nothing is uploaded to us. Your files and databases are served over your own network, only while the server is on.
            </p>
            <div style={{ display: "flex", gap: "0.7rem", justifyContent: "center", flexWrap: "wrap", marginBottom: "2.2rem" }}>
              {["PHP 8.4", "SQLite", "MySQL client", "QR sharing", "Foreground service", "26 languages"].map((t) => (
                <span key={t} style={{ background: PANEL, border: `1px solid ${AMBER_D}`, color: INK, fontWeight: 700, fontSize: "0.95rem", padding: "0.55rem 1.1rem", borderRadius: 999, fontFamily: MONO }}>{t}</span>
              ))}
            </div>
            <PlayButton />
          </div>
        </section>

        {/* FAQ */}
        <section style={{ position: "relative", maxWidth: 880, margin: "0 auto", padding: "3.5rem 1.5rem 1rem" }}>
          <h2 style={{ fontFamily: SANS, fontWeight: 800, fontSize: "clamp(2rem,5vw,3rem)", letterSpacing: "-1.5px", color: INK, margin: 0 }}>
            Frequently asked questions
          </h2>
          <p style={{ fontFamily: SANS, fontSize: "1.18rem", color: MUTE, margin: "0.8rem 0 2.4rem", maxWidth: 600, lineHeight: 1.5 }}>
            Everything about running a web server from your phone with HostPilot.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {FAQS.map((f) => (
              <div key={f.q} style={{ background: PANEL, border: "1px solid #E1DCCE", borderRadius: "1rem", padding: "1.3rem 1.5rem", boxShadow: "0 1px 2px rgba(52,44,26,0.06), 0 4px 14px rgba(52,44,26,0.05)" }}>
                <h3 style={{ fontFamily: SANS, fontWeight: 700, fontSize: "1.18rem", color: INK, margin: "0 0 0.5rem", display: "flex", gap: "0.6rem" }}>
                  <span style={{ color: AMBER, fontFamily: MONO }}>$</span>{f.q}
                </h3>
                <p style={{ fontFamily: SANS, fontSize: "1.04rem", color: "#3D4B59", lineHeight: 1.6, margin: 0 }}>{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FOOTER */}
        <footer style={{ position: "relative", maxWidth: 1180, margin: "0 auto", padding: "2.5rem 1.5rem 6rem", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", color: MUTE, fontSize: "0.93rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <img src={B + "/logo.png"} alt="" width={30} height={30} style={{ borderRadius: 8 }} />
            <span>© Cosmovex · HostPilot</span>
          </div>
          <div style={{ display: "flex", gap: "1.4rem" }}>
            <a href="/apps/hostpilot/privacy-policy" style={{ color: MUTE, textDecoration: "none" }}>Privacy</a>
            <a href="/apps/hostpilot/support" style={{ color: MUTE, textDecoration: "none" }}>Support</a>
            <a href={PLAY} onClick={playClick} style={{ color: AMBER, fontWeight: 700, textDecoration: "none" }}>Get the app</a>
          </div>
        </footer>
      </div>

      {/* STICKY INSTALL BAR */}
      {showBar && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1000, display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.65rem 1rem calc(0.65rem + env(safe-area-inset-bottom))", background: "rgba(251,250,246,0.96)", backdropFilter: "blur(14px)", borderTop: "1px solid #E1DCCE" }}>
          <img src={B + "/logo.png"} alt="" width={38} height={38} style={{ borderRadius: 9 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: INK, fontWeight: 700, fontSize: "0.95rem" }}>HostPilot</div>
            <div style={{ color: MUTE, fontSize: "0.78rem", fontFamily: MONO }}>php · sqlite · stays running</div>
          </div>
          <PlayButton small />
        </div>
      )}

      <style>{`
        @keyframes hpIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        .hp-row { animation: hpIn 260ms ease-out; }
        @media (prefers-reduced-motion: reduce) { .hp-row { animation: none; } }
        @media (max-width: 820px){
          .hp-hero{ grid-template-columns: 1fr !important; text-align:center; }
          .hp-hero > div:last-child{ margin-top:1.5rem; }
          .hp-hero > div:first-child > div{ margin-left:auto; margin-right:auto; }
        }
      `}</style>
    </>
  );
};

export default HostPilotLanding;
